import { z } from "zod";

import { jurisdictionProfileSchema } from "../services/cableContract";
import type { ValidationLabels } from "../utils/validationMessages";
import {
  LOAD_SOURCE_LABELS,
  PF_CORRECTION_LABELS,
  SHORT_CIRCUIT_SOURCE_LABELS,
} from "./pfCorrectionLabels";
import { CASE_LABELS, FAULT_TYPE_LABELS } from "./starterCoordinationDraft";

// Draft model of the power factor correction form (EOS-11a, A32): what the user is typing,
// kept apart from the components so the payload rules are tested without a browser.
//
// Every value is held as the string the user typed. The load figures come from one of two
// places: a stored EOS-02 load run of the active revision (LINKED, the default) or typed
// figures with their source (TYPED, a review when the source is blank). The short-circuit
// power for the resonance screening comes from a stored EOS-04 fault run (LINKED, the
// default), a typed figure (TYPED) or nowhere (NONE: the screening is not made, a review).
// The step sizes and the characteristic orders are typed as comma-separated lists. A blank
// required figure is sent as "" for the schema to report by its on-screen label; an
// optional one is left out.

/** The module key the draft is kept under in the browser tab (see useStudyDraft). */
export const PF_CORRECTION_DRAFT_MODULE = "pf-correction";

export const LOAD_SOURCES = ["LINKED", "TYPED"] as const;
export type LoadSourceChoice = (typeof LOAD_SOURCES)[number];

export const SHORT_CIRCUIT_SOURCES = ["LINKED", "TYPED", "NONE"] as const;
export type ShortCircuitSourceChoice = (typeof SHORT_CIRCUIT_SOURCES)[number];

export const LOAD_SOURCE_CHOICE_LABELS: Record<LoadSourceChoice, string> = {
  LINKED: "Linked load run",
  TYPED: "Typed figures (review required without a source)",
};

export const SHORT_CIRCUIT_SOURCE_CHOICE_LABELS: Record<ShortCircuitSourceChoice, string> = {
  LINKED: "Linked fault run",
  TYPED: "Typed value (review required without a source)",
  NONE: "Not established (resonance not screened, review required)",
};

export { CASE_LABELS, FAULT_TYPE_LABELS, LOAD_SOURCE_LABELS, SHORT_CIRCUIT_SOURCE_LABELS };

export type PfCorrectionDraft = {
  code: string;
  name: string;
  busCode: string;
  systemNominalVoltageV: string;
  frequencyHz: string;
  loadSource: LoadSourceChoice;
  loadRunId: string;
  typedActivePowerKw: string;
  typedPresentPowerFactor: string;
  typedLoadSource: string;
  targetPowerFactor: string;
  bankRatedVoltageV: string;
  bankDeclaredRatingKvar: string;
  // Comma-separated kvar figures at the rated voltage, largest first or any order.
  stepSizesKvar: string;
  reactorRatioPercent: string;
  shortCircuitSource: ShortCircuitSourceChoice;
  faultRunId: string;
  typedShortCircuitPowerKva: string;
  typedShortCircuitSource: string;
  // Comma-separated integer orders present in the loads, e.g. "5, 7, 11, 13".
  characteristicOrders: string;
  jurisdictionProfile: string;
  notes: string;
};

// The shape a draft kept in the browser tab must still have to be restored: exactly the
// fields above, typed text as strings and the choices as values this form knows. A draft
// saved by an older form fails here and is dropped instead of half-filling this one.
export const pfCorrectionDraftSchema = z
  .object({
    code: z.string(),
    name: z.string(),
    busCode: z.string(),
    systemNominalVoltageV: z.string(),
    frequencyHz: z.string(),
    loadSource: z.enum(LOAD_SOURCES),
    loadRunId: z.string(),
    typedActivePowerKw: z.string(),
    typedPresentPowerFactor: z.string(),
    typedLoadSource: z.string(),
    targetPowerFactor: z.string(),
    bankRatedVoltageV: z.string(),
    bankDeclaredRatingKvar: z.string(),
    stepSizesKvar: z.string(),
    reactorRatioPercent: z.string(),
    shortCircuitSource: z.enum(SHORT_CIRCUIT_SOURCES),
    faultRunId: z.string(),
    typedShortCircuitPowerKva: z.string(),
    typedShortCircuitSource: z.string(),
    characteristicOrders: z.string(),
    jurisdictionProfile: jurisdictionProfileSchema,
    notes: z.string(),
  })
  .strict();

// Only the defaults the backend itself has are filled in: the IN profile and 50 Hz is NOT
// defaulted (the frequency is the project's). Both sources start on the linked run with
// no run chosen yet; the target power factor waits for the engineer (D3).
export function createInitialPfCorrectionDraft(): PfCorrectionDraft {
  return {
    code: "",
    name: "",
    busCode: "",
    systemNominalVoltageV: "",
    frequencyHz: "",
    loadSource: "LINKED",
    loadRunId: "",
    typedActivePowerKw: "",
    typedPresentPowerFactor: "",
    typedLoadSource: "",
    targetPowerFactor: "",
    bankRatedVoltageV: "",
    bankDeclaredRatingKvar: "",
    stepSizesKvar: "",
    reactorRatioPercent: "",
    shortCircuitSource: "LINKED",
    faultRunId: "",
    typedShortCircuitPowerKva: "",
    typedShortCircuitSource: "",
    characteristicOrders: "",
    jurisdictionProfile: "IN",
    notes: "",
  };
}

// An optional text or decimal field: sent trimmed, or left out when blank.
function optional(key: string, typed: string): Record<string, string> {
  const value = typed.trim();
  return value === "" ? {} : { [key]: value };
}

/** "50, 25, 12.5" -> ["50", "25", "12.5"]; blank -> []. */
export function splitList(typed: string): string[] {
  return typed
    .split(/[,\s]+/)
    .map((item) => item.trim())
    .filter((item) => item !== "");
}

// The orders as integers; a token that is not a whole number is kept as typed so the schema
// names it under its label rather than this builder judging it.
function orderTokens(typed: string): (number | string)[] {
  return splitList(typed).map((token) => (/^\d+$/.test(token) ? Number(token) : token));
}

// The load figures as chosen on screen: the linked run's id alone, or the typed block
// alone. A blank run id or a blank typed figure is sent as "" so the schema names it.
function loadPayload(draft: PfCorrectionDraft): Record<string, unknown> {
  if (draft.loadSource === "TYPED") {
    return {
      typed_load: {
        active_power_kw: draft.typedActivePowerKw.trim(),
        present_power_factor: draft.typedPresentPowerFactor.trim(),
        ...optional("source", draft.typedLoadSource),
      },
    };
  }
  return { load_run_id: draft.loadRunId.trim() };
}

// The short-circuit power as chosen: the linked run's id, the typed block, or nothing.
function shortCircuitPayload(draft: PfCorrectionDraft): Record<string, unknown> {
  if (draft.shortCircuitSource === "TYPED") {
    return {
      typed_short_circuit_power: {
        short_circuit_power_kva: draft.typedShortCircuitPowerKva.trim(),
        ...optional("source", draft.typedShortCircuitSource),
      },
    };
  }
  if (draft.shortCircuitSource === "LINKED") {
    return { fault_run_id: draft.faultRunId.trim() };
  }
  return {};
}

// The request body as the form will send it. It is validated afterwards with
// pfCorrectionRunCreateRequestSchema, so this function does not judge the values.
export function buildPfCorrectionRunPayload(
  draft: PfCorrectionDraft,
  projectRevisionId?: string,
): Record<string, unknown> {
  const steps = splitList(draft.stepSizesKvar);
  const orders = orderTokens(draft.characteristicOrders);
  return {
    study: {
      code: draft.code.trim(),
      name: draft.name.trim(),
      bus_code: draft.busCode.trim(),
      system_nominal_voltage_v: draft.systemNominalVoltageV.trim(),
      frequency_hz: draft.frequencyHz.trim(),
      ...loadPayload(draft),
      target_power_factor: draft.targetPowerFactor.trim(),
      bank: {
        rated_voltage_v: draft.bankRatedVoltageV.trim(),
        ...optional("declared_rating_kvar", draft.bankDeclaredRatingKvar),
        ...(steps.length > 0 ? { step_sizes_kvar: steps } : {}),
      },
      ...optional("reactor_ratio_percent", draft.reactorRatioPercent),
      ...shortCircuitPayload(draft),
      ...(orders.length > 0 ? { characteristic_orders: orders } : {}),
      ...optional("jurisdiction_profile", draft.jurisdictionProfile),
    },
    ...(projectRevisionId === undefined ? {} : { project_revision_id: projectRevisionId }),
    ...optional("notes", draft.notes),
  };
}

// The on-screen labels of the form, keyed by the request path (pfCorrectionLabels.ts, shared
// with the report's inputs section).
export const PF_CORRECTION_DRAFT_LABELS: ValidationLabels = PF_CORRECTION_LABELS;
export { PF_CORRECTION_LABELS };
